import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ArrowUturnLeftIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import ordersService from '../../services/orders.service';
import MultiSelect, { MultiSelectOption } from '../../components/MultiSelect';
import DateTimePicker from '../../components/DateTimePicker';
import { metricsDateShortcuts } from './dispatcherMetrics/dateShortcuts';
import FilterBar from '../../components/FilterBar';
import MetricMatrixTable from './dispatcherMetrics/MetricMatrixTable';
import TurnoverTable, { TurnoverCell } from './dispatcherMetrics/TurnoverTable';
import TurnoverDetailModal from './dispatcherMetrics/TurnoverDetailModal';
import OrderMetricSelector, { MetricGroup, storedGroup, groupLabel, groupedOrderRows, groupedMetric } from './dispatcherMetrics/OrderMetricSelector';
import { KEY_STATUS_OPTIONS } from '../order-management/quoteColors';
import { SERVICE_FAILURE_TYPE_FILTER_OPTIONS } from '../../constants/serviceFailureTypes';
import DetailModal, { DetailKind } from './dispatcherMetrics/DetailModal';
import ProDetailSlideOver from '../../components/pro/ProDetailSlideOver';
import RescheduleEditModal from '../pro/components/RescheduleEditModal';
import { ViewedProsProvider } from '../../contexts/ViewedProsContext';
import {
  MetricsResponse,
  MetricDef,
  METRICS,
  ORDER_METRICS,
  CUSTOMER_OTD_METRIC,
  CUSTOMER_PASS_METRIC,
  CUSTOMER_SERVICE_FAILURES_METRIC,
  CUSTOMER_UNCODED_METRIC,
  TURNOVER_METRIC,
  TurnoverRow,
  TurnoverDriver,
  turnoverPeriods,
  periodMonths,
  periodRange,
  AnyRow,
  ServiceFailureDetail,
  LateDetail,
  UncodedDetail,
  serviceFailureDetails,
  serviceFailurePros,
  lateDetailRows,
  latePros,
  uncodedDetailRows,
  uncodedPros,
  passFailDetails,
  passFailPros,
  buildView,
  filterRowsByFailureType,
  withFailureTypeFilter,
  OrderRow,
  bucketSignature,
  fmtDateKey,
  fmtMD,
  parseLocal,
  isDateKey,
} from './dispatcherMetrics/logic';

/**
 * Per-dispatcher operational metrics. Each metric is a filterable/sortable/
 * exportable TanTable (dispatcher rows × date columns). A metric selector picks
 * which table is shown — TanTable owns a full-height container, so one at a time
 * reads better than stacking all seven.
 *
 * This is the only home for these metrics: the Home dashboard card that used to
 * render them (components/DispatcherMetricsCard) was removed on 2026-09-16.
 */

type ViewKind = 'driver' | 'order' | 'customer' | 'turnover';
// Which legs the Dispatches metric counts. 'delivery' = the order's delivery
// leg only (server-side: legsall.legnum = ordersall.deliveryleg). Only the
// Dispatches tab reads it; the other metrics are unaffected.
type DispatchLegScope = 'all' | 'delivery';
const DISPATCH_LEG_SCOPE_OPTIONS: { value: DispatchLegScope; label: string }[] = [
  { value: 'all', label: 'All legs' },
  { value: 'delivery', label: 'Delivery legs' },
];
const DISPATCHES_DELIVERY_DESCRIPTION =
  "Delivery legs dispatched (each order's delivery leg only), attributed to each dispatcher and counted by the leg's appointment date.";
interface MetricTab {
  key: string;
  label: string;
  group: string;
  metric: MetricDef;
  kind: ViewKind;
}
const METRIC_TABS: MetricTab[] = [
  ...METRICS.map((m) => ({ key: m.key, label: m.label, group: 'Driver', metric: m, kind: 'driver' as const })),
  // Turnover sits with the other Driver metrics but is its own kind: it comes off
  // the data warehouse on a quarterly axis, so it has its own fetch and view
  // model rather than riding the daily (dispatcher × date) grid.
  { key: TURNOVER_METRIC.key, label: TURNOVER_METRIC.label, group: 'Driver', metric: TURNOVER_METRIC, kind: 'turnover' as const },
  ...ORDER_METRICS.map((m) => ({ key: m.key, label: m.label, group: 'Orders', metric: m, kind: 'order' as const })),
  // Customer group mirrors the Orders order: Service Failures / Uncoded / OTD.
  { key: CUSTOMER_SERVICE_FAILURES_METRIC.key, label: CUSTOMER_SERVICE_FAILURES_METRIC.label, group: 'Customer', metric: CUSTOMER_SERVICE_FAILURES_METRIC, kind: 'customer' as const },
  { key: CUSTOMER_PASS_METRIC.key, label: CUSTOMER_PASS_METRIC.label, group: 'Customer', metric: CUSTOMER_PASS_METRIC, kind: 'customer' as const },
  { key: CUSTOMER_UNCODED_METRIC.key, label: CUSTOMER_UNCODED_METRIC.label, group: 'Customer', metric: CUSTOMER_UNCODED_METRIC, kind: 'customer' as const },
  { key: CUSTOMER_OTD_METRIC.key, label: CUSTOMER_OTD_METRIC.label, group: 'Customer', metric: CUSTOMER_OTD_METRIC, kind: 'customer' as const },
];
const SELECTABLE_TABS = METRIC_TABS.filter((t) => t.kind !== 'customer').map((t) => ({ ...t, group: t.group === 'LC' ? 'Orders' : t.group, label: t.label.replace('OTD by LC', 'On Time Delivery') }));

// Persisted control-bar state (distinct key so it never clobbers the card's).
const FILTERS_KEY = 'dispatcherMetricsReportFilters';
interface StoredFilters {
  regions?: string[];
  preference?: 'all' | 'local' | 'otr';
  excludeWeekends?: boolean;
  showDetails?: boolean;
  startDate?: string;
  endDate?: string;
  excludedDispatchers?: string[];
  viewMode?: 'daily' | 'weekly';
  metric?: string;
  grouping?: MetricGroup;
  dispatchLegScope?: DispatchLegScope;
  keyStatuses?: string[];
  sfTypes?: string[];
  turnoverPeriod?: string;
}
const loadStoredFilters = (): StoredFilters => {
  try {
    const parsed = JSON.parse(localStorage.getItem(FILTERS_KEY) || '{}');
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    return {};
  }
};

const defaultStartDate = (): Date => {
  const d = new Date();
  d.setDate(d.getDate() - 6);
  return d;
};
// Default end date is yesterday — today is still in progress, so the range
// defaults to complete days. (The user can still extend it to today.)
const defaultEndDate = (): Date => {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d;
};
// Latest selectable date is today — future dates stay excluded, but the range
// may be extended into the current (in-progress) day.
const maxSelectableDate = (): Date => {
  const d = new Date();
  d.setHours(23, 59, 59, 999);
  return d;
};

const DispatcherMetricsContent: React.FC = () => {
  const [stored] = useState(loadStoredFilters);
  const [isDark, setIsDark] = useState<boolean>(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const check = () => setIsDark(document.documentElement.classList.contains('dark'));
    const obs = new MutationObserver(check);
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => obs.disconnect();
  }, []);

  const [regionOptions, setRegionOptions] = useState<MultiSelectOption[]>([]);
  const [selectedRegions, setSelectedRegions] = useState<string[]>(
    Array.isArray(stored.regions) ? stored.regions : []
  );
  const [startDate, setStartDate] = useState<Date | null>(() =>
    isDateKey(stored.startDate) ? parseLocal(stored.startDate) : defaultStartDate()
  );
  const [endDate, setEndDate] = useState<Date | null>(() =>
    isDateKey(stored.endDate) ? parseLocal(stored.endDate) : defaultEndDate()
  );
  const dateShortcuts = useMemo(() => metricsDateShortcuts((start, end) => {
    setStartDate(start);
    setEndDate(end);
  }), []);
  const [excludeWeekends, setExcludeWeekends] = useState(stored.excludeWeekends === true);
  const [preference, setPreference] = useState<'all' | 'local' | 'otr'>(
    stored.preference === 'local' || stored.preference === 'otr' ? stored.preference : 'all'
  );
  const [showDetails, setShowDetails] = useState(stored.showDetails === true);
  const [viewMode, setViewMode] = useState<'daily' | 'weekly'>(
    stored.viewMode === 'weekly' ? 'weekly' : 'daily'
  );
  const [excludedDispatchers, setExcludedDispatchers] = useState<string[]>(
    Array.isArray(stored.excludedDispatchers) ? stored.excludedDispatchers : []
  );
  const [grouping, setGrouping] = useState<MetricGroup>(() => storedGroup(stored.grouping,
    stored.metric?.includes('customer') ? 'customer' : 'dispatcher'));
  const [selectedMetric, setSelectedMetric] = useState<string>(
    (() => {
      const saved = METRIC_TABS.find((t) => t.key === stored.metric);
      return saved?.kind === 'customer'
        ? SELECTABLE_TABS.find((t) => t.metric?.detailKind === saved.metric?.detailKind)?.key ?? SELECTABLE_TABS[0].key
        : saved?.key ?? SELECTABLE_TABS[0].key;
    })()
  );
  const [dispatchLegScope, setDispatchLegScope] = useState<DispatchLegScope>(
    stored.dispatchLegScope === 'delivery' ? 'delivery' : 'all'
  );
  // Service-failure types to count (empty = all). Only the Service Failures
  // tables read it, so the control shows only while one of them is open.
  const [selectedSfTypes, setSelectedSfTypes] = useState<string[]>(
    Array.isArray(stored.sfTypes) ? stored.sfTypes : []
  );
  // Customer key status (cms.customers.keyaccount): empty = all customers. Scopes
  // every order-based metric server-side — SFs, OTD, uncoded, dispatches, legs
  // and the customer tables — to orders for customers with a selected status.
  const [selectedKeyStatuses, setSelectedKeyStatuses] = useState<string[]>(
    Array.isArray(stored.keyStatuses) ? stored.keyStatuses : []
  );

  // Service Failures / OTD detail modal + Pro drawer.
  const [detailModal, setDetailModal] = useState<{ kind: DetailKind; title: string; rows: (ServiceFailureDetail | LateDetail | UncodedDetail)[]; pros: number[] } | null>(null);
  const [selectedPro, setSelectedPro] = useState<number | null>(null);
  // The page's range, for the drill-down's added-order-columns lookup.
  const pickerRange = useMemo<[string, string] | null>(
    () => (startDate && endDate ? [fmtDateKey(startDate), fmtDateKey(endDate)] : null),
    [startDate, endDate]
  );
  // New Service Failure record (from an Uncoded planned date), seeded with the pro + planned date.
  const [sfCreate, setSfCreate] = useState<
    { pro: number; date: string; billto?: number | null } | null
  >(null);
  const openDetail = useCallback((rows: AnyRow[], kind: DetailKind) => {
    const names = Array.from(new Set(rows.map((r) => r.dispatcher).filter(Boolean)));
    const dates = Array.from(new Set(rows.map((r) => r.date)));
    const who = names.length === 1 ? names[0] : '';
    const when = dates.length === 1 ? fmtMD(dates[0]) : '';
    const detailRows = kind === 'otd'
      ? lateDetailRows(rows)
      : kind === 'uncoded'
        ? uncodedDetailRows(rows)
        : kind === 'servicePass'
          ? passFailDetails(rows)
          : serviceFailureDetails(rows);
    const pros = kind === 'otd'
      ? Array.from(new Set(latePros(rows)))
      : kind === 'uncoded'
        ? uncodedPros(rows)
        : kind === 'servicePass'
          ? passFailPros(rows)
          : serviceFailurePros(rows);
    setDetailModal({ kind, title: [who, when].filter(Boolean).join(', '), rows: detailRows, pros });
  }, []);

  const [hideOpen, setHideOpen] = useState(false);
  const [hideSearch, setHideSearch] = useState('');
  const hideRef = useRef<HTMLDivElement | null>(null);
  const maxDate = useMemo(maxSelectableDate, []);

  const [data, setData] = useState<MetricsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Turnover is fetched on its own, only while its tab is open: it is a data
  // warehouse round trip on a different (quarterly) axis, so folding it into the
  // main payload would make every other tab wait on it.
  // Which quarter tab is open: `${year}|${quarter}`, '' until the rows arrive
  // and the latest complete quarter is picked.
  const [turnoverPeriod, setTurnoverPeriod] = useState<string>(stored.turnoverPeriod ?? '');
  const [turnoverDrivers, setTurnoverDrivers] = useState<Record<string, TurnoverDriver>>({});
  const [driverModal, setDriverModal] = useState<{ title: string; rows: TurnoverDriver[]; eventLabel: string } | null>(null);
  const [turnoverRows, setTurnoverRows] = useState<TurnoverRow[] | null>(null);
  const [turnoverLoading, setTurnoverLoading] = useState(false);
  const [turnoverError, setTurnoverError] = useState<string | null>(null);
  const turnoverAbortRef = useRef<AbortController | null>(null);
  // What the loaded rows were fetched for, so leaving the tab and coming back
  // does not re-run the warehouse query for the same year and regions.
  const turnoverLoadedRef = useRef<string | null>(null);

  // Persist the control bar.
  useEffect(() => {
    try {
      const filters: StoredFilters = {
        regions: selectedRegions,
        preference,
        excludeWeekends,
        showDetails,
        startDate: startDate ? fmtDateKey(startDate) : undefined,
        endDate: endDate ? fmtDateKey(endDate) : undefined,
        excludedDispatchers,
        viewMode,
        metric: selectedMetric,
        grouping,
        dispatchLegScope,
        keyStatuses: selectedKeyStatuses,
        sfTypes: selectedSfTypes,
        turnoverPeriod,
      };
      localStorage.setItem(FILTERS_KEY, JSON.stringify(filters));
    } catch { /* storage unavailable — non-fatal */ }
  }, [selectedRegions, preference, excludeWeekends, showDetails, startDate, endDate, excludedDispatchers, viewMode, selectedMetric, dispatchLegScope, selectedKeyStatuses, grouping, selectedSfTypes, turnoverPeriod]);

  const resetFilters = () => {
    setSelectedRegions([]);
    setPreference('all');
    setSelectedKeyStatuses([]);
    setSelectedSfTypes([]);
    setExcludeWeekends(false);
    setShowDetails(false);
    setViewMode('daily');
    setDispatchLegScope('all');
    setStartDate(defaultStartDate());
    setEndDate(defaultEndDate());
  };

  // Region options.
  useEffect(() => {
    let active = true;
    ordersService
      .getRegions()
      .then((res: any) => {
        if (!active) return;
        const regions: string[] = res?.data?.regions || res?.regions || [];
        setRegionOptions(regions.map((r) => ({ value: r, label: r })));
      })
      .catch(() => { /* non-fatal */ });
    return () => { active = false; };
  }, []);

  // Close the Hide Dispatchers popup on outside click.
  useEffect(() => {
    if (!hideOpen) return;
    const onDown = (e: MouseEvent) => {
      if (hideRef.current && !hideRef.current.contains(e.target as Node)) setHideOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [hideOpen]);

  // Data fetch on region / date / preference / hide change.
  const fetchData = useCallback(async () => {
    if (!startDate || !endDate) return;
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setLoading(true);
    setError(null);
    try {
      const params: Record<string, string> = {
        start_date: fmtDateKey(startDate),
        end_date: fmtDateKey(endDate),
      };
      if (selectedRegions.length > 0) params.region = selectedRegions.join(',');
      if (preference !== 'all') params.preference = preference;
      if (excludedDispatchers.length > 0) params.hide_dispatchers = excludedDispatchers.join(',');
      // Server-side narrowing of the Dispatches count; omitted = all legs.
      if (dispatchLegScope === 'delivery') params.dispatch_leg_scope = 'delivery';
      // Customer key status; omitted = all customers.
      if (selectedKeyStatuses.length > 0) params.key_status = selectedKeyStatuses.join(',');
      const res = await api.get('/reporting/dispatcher-metrics', { params, signal: controller.signal });
      setData(res.data);
    } catch (e: any) {
      if (e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED') {
        setError('Failed to load dispatcher metrics');
      }
    } finally {
      if (abortRef.current === controller) setLoading(false);
    }
  }, [startDate, endDate, selectedRegions, preference, excludedDispatchers, dispatchLegScope, selectedKeyStatuses]);

  useEffect(() => {
    fetchData();
    return () => abortRef.current?.abort();
  }, [fetchData]);

  // Driver turnover: whole quarters off the warehouse, narrowed only by region
  // (it carries no customer or driver-preference dimension).
  const showTurnover = selectedMetric === TURNOVER_METRIC.key;
  useEffect(() => {
    if (!showTurnover) return;
    const regionKey = selectedRegions.join(',');
    const fetchKey = `rolling4|${regionKey}`;
    if (turnoverLoadedRef.current === fetchKey) return;
    turnoverAbortRef.current?.abort();
    const controller = new AbortController();
    turnoverAbortRef.current = controller;
    setTurnoverLoading(true);
    setTurnoverError(null);
    const today = new Date();
    const params: Record<string, string> = {
      // start/end are required by the endpoint but unused by this section — the
      // window is what selects the quarters.
      start_date: fmtDateKey(today),
      end_date: fmtDateKey(today),
      sections: 'turnover',
      turnover_window: 'rolling4',
    };
    if (regionKey) params.region = regionKey;
    api
      .get('/reporting/dispatcher-metrics', { params, signal: controller.signal })
      .then((res) => {
        turnoverLoadedRef.current = fetchKey;
        setTurnoverRows(res.data?.turnover_rows ?? []);
        setTurnoverDrivers(res.data?.turnover_drivers ?? {});
      })
      .catch((e: any) => {
        if (e?.name !== 'CanceledError' && e?.code !== 'ERR_CANCELED') {
          setTurnoverError('Failed to load driver turnover');
        }
      })
      .finally(() => {
        if (turnoverAbortRef.current === controller) setTurnoverLoading(false);
      });
    return () => controller.abort();
  }, [showTurnover, selectedRegions]);

  // Hide-popup names: everyone currently loaded, plus already-hidden picks.
  const hideNames = useMemo<string[]>(() => {
    const names = new Set<string>(excludedDispatchers);
    for (const r of data?.rows ?? []) names.add(r.dispatcher);
    for (const r of data?.order_rows ?? []) names.add(r.dispatcher);
    for (const r of turnoverRows ?? []) names.add(r.dispatcher);
    return Array.from(names).sort((a, b) => a.localeCompare(b));
  }, [data, excludedDispatchers, turnoverRows]);

  // Derived views.
  const excludedSet = useMemo(() => new Set(excludedDispatchers), [excludedDispatchers]);
  const weekly = viewMode === 'weekly';
  const currentTab = METRIC_TABS.find((t) => t.key === selectedMetric) ?? METRIC_TABS[0];
  // The SF Type filter narrows the failures a Service Failures table counts, so
  // it only means anything on those tabs — applied here rather than in the fetch
  // because every failure's type already rides along in the payload.
  const isServiceFailuresTab = currentTab.metric?.detailKind === 'serviceFailures';
  const narrowToSfTypes = useCallback(
    (rows: OrderRow[]): OrderRow[] =>
      isServiceFailuresTab && selectedSfTypes.length > 0
        ? (filterRowsByFailureType(rows, selectedSfTypes) as OrderRow[])
        : rows,
    [isServiceFailuresTab, selectedSfTypes],
  );
  const view = useMemo(
    () => (data ? buildView(data.rows.filter((r) => !excludedSet.has(r.dispatcher)), data.dates, excludeWeekends, weekly) : null),
    [data, excludeWeekends, excludedSet, weekly]
  );
  const groupedView = useMemo(() => {
    if (!data) return null;
    let rows = groupedOrderRows(data, grouping);
    if (grouping === 'dispatcher') rows = rows.filter((r) => !excludedSet.has(r.dispatcher));
    return buildView(narrowToSfTypes(rows), data.dates, excludeWeekends, weekly);
  }, [data, grouping, excludeWeekends, weekly, excludedSet, narrowToSfTypes]);

  // The quarters the payload covers, plus the roll-up, for the tab strip.
  const turnoverTabs = useMemo(() => turnoverPeriods(turnoverRows ?? []), [turnoverRows]);
  // Default to the latest COMPLETE quarter: the one in progress is days old for
  // most of its life and reads as a near-empty table.
  const activeTurnoverPeriod = useMemo(() => {
    if (turnoverTabs.some((t) => `${t.year}|${t.quarter}` === turnoverPeriod)) return turnoverPeriod;
    const quarters = turnoverTabs.filter((t) => t.quarter !== 0);
    const complete = quarters.slice(0, -1);
    const pick = (complete.length > 0 ? complete : quarters).slice(-1)[0];
    return pick ? `${pick.year}|${pick.quarter}` : '';
  }, [turnoverTabs, turnoverPeriod]);
  const activePeriod = useMemo(
    () => turnoverTabs.find((t) => `${t.year}|${t.quarter}` === activeTurnoverPeriod),
    [turnoverTabs, activeTurnoverPeriod]
  );
  const turnoverTableRows = useMemo(
    () => (turnoverRows ?? [])
      .filter((r) => !excludedSet.has(r.dispatcher))
      .filter((r) => `${r.period_year}|${r.quarter}` === activeTurnoverPeriod),
    [turnoverRows, excludedSet, activeTurnoverPeriod]
  );
  const openTurnoverDrivers = useCallback(
    (row: TurnoverRow, cell: TurnoverCell, columnLabel: string) => {
      const blank = (u: string): TurnoverDriver => ({
        unit: u, name: '', preference: '', start_date: '',
        in_service_since: '', term_date: '', status: '',
      });
      // Roster snapshots are unit numbers; hires and terminations are events
      // that carry their own date, which is the column the modal heads.
      //
      // Events are NOT de-duplicated: a unit hired twice in a quarter counts
      // twice, so collapsing it would leave the modal one row short of the
      // number that opened it.
      const entries = row[cell] as (string | { unit: string; date: string })[];
      const rows = entries
        .map((e) => (typeof e === 'string' ? { unit: e, date: '' } : e))
        .map((e) => ({
          ...(turnoverDrivers[e.unit] ?? blank(e.unit)),
          event_date: e.date || undefined,
          // A departure the same unit was hired back after, inside this period.
          // Read off the events rather than the driver's current status: a
          // rehire erases the termination from cms, and the status can be stale
          // besides, so it is not evidence of anything.
          rehired: cell === 'term_units'
            && row.hire_units.some((h) => h.unit === e.unit && h.date > e.date),
        }));
      setDriverModal({
        title: `${row.dispatcher}: ${columnLabel}`
          + (activePeriod ? `, ${periodRange(activePeriod.start, activePeriod.end)}` : ''),
        rows,
        eventLabel: cell === 'hire_units' ? 'Hired On' : cell === 'term_units' ? 'Left On' : '',
      });
    },
    [turnoverDrivers, activePeriod]
  );

  const currentView = currentTab.kind === 'driver' ? view : groupedView;
  const isDispatchesTab = currentTab.key === 'dispatches';
  const deliveryOnly = isDispatchesTab && dispatchLegScope === 'delivery';
  // Same metric key (so column settings persist), but the table title and the
  // description say what is actually being counted when narrowed.
  const currentMetric = useMemo<MetricDef>(
    () =>
      withFailureTypeFilter(
        deliveryOnly
          ? { ...currentTab.metric, label: `${currentTab.metric.label} (Delivery legs)`, description: DISPATCHES_DELIVERY_DESCRIPTION }
          : currentTab.kind === 'driver' || currentTab.kind === 'turnover'
            ? currentTab.metric
            : groupedMetric(currentTab.metric, grouping),
        isServiceFailuresTab ? selectedSfTypes : [],
      ),
    [currentTab, deliveryOnly, isServiceFailuresTab, selectedSfTypes, grouping]
  );

  return (
    <div className="flex flex-col h-full w-full overflow-hidden">
      <FilterBar label="Filters:">
        <div className="w-32">
          <MultiSelect
            options={regionOptions}
            value={selectedRegions}
            onChange={setSelectedRegions}
            placeholder="All Regions"
            showClearOption
            clearOptionLabel="All Regions"
            enableSearch
            size="sm"
          />
        </div>
        {!showTurnover && <div className="w-28">
          <MultiSelect
            options={[{ value: 'local', label: 'Local' }, { value: 'otr', label: 'OTR' }]}
            value={preference === 'all' ? [] : [preference]}
            onChange={(vals) => setPreference((vals.length ? vals[vals.length - 1] : 'all') as 'all' | 'local' | 'otr')}
            placeholder="All Pref"
            showClearOption
            clearOptionLabel="All"
            size="sm"
          />
        </div>}
        {!showTurnover && <div className="w-40">
          <MultiSelect
            options={KEY_STATUS_OPTIONS}
            value={selectedKeyStatuses}
            onChange={setSelectedKeyStatuses}
            placeholder="All Key Status"
            showClearOption
            clearOptionLabel="All Key Status"
            enableSearch
            size="sm"
          />
        </div>}
        {!showTurnover && <div className="flex items-center gap-1">
          <div className="w-32">
            <DateTimePicker
              mode="date"
              selectionType="single"
              calendarShortcuts={dateShortcuts}
              value={startDate}
              onChange={(v) => {
                const next = (v as Date | null) ?? null;
                setStartDate(next);
                if (next && endDate && fmtDateKey(next) > fmtDateKey(endDate)) {
                  setEndDate(next);
                }
              }}
              inputVariant="compact"
              labelType="none"
              placeholder="Start"
              maxDate={maxDate}
              autoApply
              className="w-full"
            />
          </div>
          <span className="text-muted">–</span>
          <div className="w-32">
            <DateTimePicker
              mode="date"
              selectionType="single"
              calendarShortcuts={dateShortcuts}
              value={endDate}
              onChange={(v) => {
                const next = (v as Date | null) ?? null;
                setEndDate(next && startDate && fmtDateKey(next) < fmtDateKey(startDate) ? startDate : next);
              }}
              inputVariant="compact"
              labelType="none"
              placeholder="End"
              maxDate={maxDate}
              autoApply
              className="w-full"
            />
          </div>
        </div>}

        {/* Daily / Weekly column granularity. */}
        {!showTurnover && <div className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden">
          {(['daily', 'weekly'] as const).map((mode) => (
            <button
              key={mode}
              type="button"
              onClick={() => setViewMode(mode)}
              className={`px-3 py-1 text-xs font-medium transition-colors ${
                viewMode === mode
                  ? 'bg-blue-600 text-white'
                  : 'bg-panel text-label hover:bg-gray-100 dark:hover:bg-gray-700'
              }`}
            >
              {mode === 'daily' ? 'Daily' : 'Weekly'}
            </button>
          ))}
        </div>}
        {/* Turnover's columns are calendar quarters, so the range picker and the
            daily/weekly toggle above it have nothing to act on — say what the
            table is showing instead of leaving dead controls in the bar. */}
        {/* Hide Dispatchers popover. */}
        <div ref={hideRef} className="relative">
          <button
            type="button"
            onClick={() => { setHideOpen((o) => !o); setHideSearch(''); }}
            className="text-xs text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300"
          >
            Hide Dispatchers{excludedDispatchers.length > 0 ? ` (${excludedDispatchers.length})` : ''}
          </button>
          {hideOpen && (
            <div className="absolute top-full left-0 z-40 mt-1 w-60 max-h-80 flex flex-col bg-panel border border-gray-300 dark:border-gray-600 rounded-lg shadow-xl p-2">
              <input
                type="text"
                value={hideSearch}
                onChange={(e) => setHideSearch(e.target.value)}
                placeholder="Search dispatchers…"
                autoFocus
                className="mb-1.5 px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-body outline-none"
              />
              <div className="overflow-y-auto flex-1">
                {hideNames
                  .filter((n) => n.toLowerCase().includes(hideSearch.trim().toLowerCase()))
                  .map((n) => (
                    <label key={n} className="flex items-center gap-1.5 px-1 py-0.5 rounded cursor-pointer select-none text-body hover:bg-gray-100 dark:hover:bg-gray-700">
                      <input
                        type="checkbox"
                        checked={excludedDispatchers.includes(n)}
                        onChange={(e) =>
                          setExcludedDispatchers((prev) =>
                            e.target.checked ? [...prev, n] : prev.filter((x) => x !== n)
                          )
                        }
                      />
                      <span className="text-xs">{n}</span>
                    </label>
                  ))}
                {hideNames.length === 0 && (
                  <div className="text-xs text-gray-400 dark:text-gray-500 px-1 py-0.5">No dispatchers loaded yet.</div>
                )}
              </div>
              {excludedDispatchers.length > 0 && (
                <button
                  type="button"
                  onClick={() => setExcludedDispatchers([])}
                  className="mt-1.5 px-2 py-1 text-xs rounded border border-gray-300 dark:border-gray-600 text-label hover:bg-gray-100 dark:hover:bg-gray-700"
                >
                  Unhide all ({excludedDispatchers.length})
                </button>
              )}
            </div>
          )}
        </div>

        {!showTurnover && <label className="flex items-center gap-1.5 cursor-pointer select-none text-xs text-label">
          <input type="checkbox" checked={excludeWeekends} onChange={(e) => setExcludeWeekends(e.target.checked)} />
          Exclude weekends
        </label>}
        <label className="flex items-center gap-1.5 cursor-pointer select-none text-xs text-label">
          <input type="checkbox" checked={showDetails} onChange={(e) => setShowDetails(e.target.checked)} />
          Show details
        </label>
        <button
          type="button"
          onClick={resetFilters}
          title="Reset filters"
          aria-label="Reset filters"
          className="p-1 rounded text-muted hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-700"
        >
          <ArrowUturnLeftIcon className="w-4 h-4" />
        </button>
      </FilterBar>

      {/* In-progress caveat — its own line below the filter bar, shown only
          while the range reaches into today (deliveries still landing). */}
      {!showTurnover && endDate && fmtDateKey(endDate) === fmtDateKey(maxDate) && (
        <div className="px-4 py-1 text-xs italic text-amber-600 dark:text-amber-400 bg-gray-50 dark:bg-gray-800/30 border-b border-gray-200 dark:border-gray-700">
          Today is still in progress — not fully accurate until the next day.
        </div>
      )}

      <OrderMetricSelector
        options={SELECTABLE_TABS}
        metric={selectedMetric}
        onMetricChange={setSelectedMetric}
        group={grouping}
        allowDeliveryLocation
        onGroupChange={setGrouping}
        showGroup={currentTab.kind !== 'driver' && currentTab.kind !== 'turnover'}
      />

      {/* Description of the selected metric, plus the controls that narrow only
          that metric (Dispatches leg scope, Service Failures type) — they live
          with what they affect rather than in the page-wide filter bar. */}
      {(currentMetric.description || isDispatchesTab || isServiceFailuresTab) && (
        <div className="flex items-center flex-wrap gap-x-3 gap-y-1 px-4 py-1.5 text-xs text-muted bg-gray-50 dark:bg-gray-800/30 border-b border-gray-200 dark:border-gray-700">
          {isDispatchesTab && (
            <div
              role="group"
              aria-label="Dispatches leg scope"
              className="inline-flex rounded-md border border-gray-300 dark:border-gray-600 overflow-hidden"
            >
              {DISPATCH_LEG_SCOPE_OPTIONS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  aria-pressed={dispatchLegScope === opt.value}
                  onClick={() => setDispatchLegScope(opt.value)}
                  className={`px-2.5 py-0.5 text-xs font-medium whitespace-nowrap border-r last:border-r-0 border-gray-300 dark:border-gray-600 transition-colors ${
                    dispatchLegScope === opt.value
                      ? 'bg-blue-600 text-white'
                      : 'bg-panel text-label hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          )}
          {/* Which failure types to count — leading the row it qualifies. Sits
              with the metric it narrows, not in the page-wide filter bar. No
              label: the placeholder already reads "All SF Types". */}
          {isServiceFailuresTab && (
            <div className="w-44">
              <MultiSelect
                options={SERVICE_FAILURE_TYPE_FILTER_OPTIONS}
                value={selectedSfTypes}
                onChange={setSelectedSfTypes}
                placeholder="All SF Types"
                showClearOption
                clearOptionLabel="All SF Types"
                enableSearch
                size="sm"
              />
            </div>
          )}
          {currentMetric.description && (
            <span>
              <span className="font-medium text-label">{currentTab.label}:</span>{' '}
              {currentMetric.description}
            </span>
          )}
        </div>
      )}

      <div className="flex-1 flex flex-col min-h-0 p-4">
        {/* A failed turnover fetch leaves rows null, so the error has to win over
            the "not loaded yet" spinner or the tab would spin forever. */}
        {(showTurnover ? turnoverLoading || (turnoverRows === null && !turnoverError) : loading) ? (
          <div className="flex-1 flex flex-col items-center justify-center gap-2.5">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 dark:border-blue-400" />
            <span className="text-sm text-muted">Loading {showTurnover ? 'turnover' : 'metrics'}…</span>
          </div>
        ) : (showTurnover ? turnoverError : error) ? (
          <div className="flex-1 flex items-center justify-center text-red-600 dark:text-red-400">{showTurnover ? turnoverError : error}</div>
        ) : showTurnover ? (
          <div className="flex flex-col h-full min-h-0">
            {/* Period sheet-tabs, sitting on the table they switch rather than
                in the page-wide filter bar — they pick what the table shows,
                not what is fetched. */}
            <div role="group" aria-label="Turnover period" className="flex items-end gap-0.5 overflow-x-auto pb-0 mb-2">
              {turnoverTabs.map((t) => {
                const key = `${t.year}|${t.quarter}`;
                const active = activeTurnoverPeriod === key;
                const months = periodMonths(t.quarter);
                return (
                  <button
                    key={key}
                    type="button"
                    aria-pressed={active}
                    onClick={() => setTurnoverPeriod(key)}
                    title={`${periodRange(t.start, t.end)}${months ? ` · ${months}` : ' · every quarter in the window'}`}
                    className={`px-3 py-1.5 text-xs font-medium rounded-t border whitespace-nowrap ${
                      active
                        ? 'bg-panel text-blue-700 dark:text-blue-300 border-gray-300 dark:border-gray-600 border-b-white dark:border-b-gray-800 -mb-px font-semibold'
                        : 'bg-gray-100 dark:bg-gray-700/60 text-gray-600 dark:text-gray-300 border-transparent hover:bg-gray-200 dark:hover:bg-gray-600'
                    }`}
                  >
                    {t.label}
                  </button>
                );
              })}
            </div>
            <TurnoverTable
              key={activeTurnoverPeriod}
              rows={turnoverTableRows}
              periodRange={periodRange(activePeriod?.start, activePeriod?.end)}
              periodLabel={activePeriod?.label ?? ''}
              onOpenDrivers={openTurnoverDrivers}
            />
          </div>
        ) : currentView ? (
          <MetricMatrixTable
            key={`${currentTab.key}|${grouping}|${dispatchLegScope}|${bucketSignature(currentView)}`}
            metric={currentMetric}
            view={currentView}
            showDetails={showDetails}
            isDark={isDark}
            axisLabel={currentTab.kind === 'driver' ? 'Dispatcher' : groupLabel(grouping)}
            onOpenDetail={openDetail}
          />
        ) : (
          <div className="flex-1 flex items-center justify-center text-gray-400 dark:text-gray-500">No data for this range/region.</div>
        )}
      </div>

      <TurnoverDetailModal
        open={driverModal !== null}
        onClose={() => setDriverModal(null)}
        title={driverModal?.title ?? ''}
        rows={driverModal?.rows ?? []}
        eventLabel={driverModal?.eventLabel ?? ''}
      />

      <DetailModal
        open={detailModal !== null}
        onClose={() => setDetailModal(null)}
        kind={detailModal?.kind ?? 'serviceFailures'}
        title={detailModal?.title ?? ''}
        rows={detailModal?.rows ?? []}
        pros={detailModal?.pros ?? []}
        range={pickerRange}
        onProClick={(pro) => setSelectedPro(pro)}
        onAddServiceFailure={(pro, date, billto) => setSfCreate({ pro, date, billto })}
      />
      {sfCreate && (
        <RescheduleEditModal
          pronumber={sfCreate.pro}
          // THD Reason is hidden without this: the editor gates it on a THD-family
          // bill-to, and these pages are not the pro screen, so the drill row is the
          // only place the bill-to comes from.
          billTo={sfCreate.billto ?? undefined}
          initialServiceFailureDate={sfCreate.date}
          onClose={() => setSfCreate(null)}
          onSaved={() => { setSfCreate(null); fetchData(); }}
        />
      )}
      <ProDetailSlideOver
        pronumber={selectedPro}
        isOpen={selectedPro !== null}
        onClose={() => setSelectedPro(null)}
      />
    </div>
  );
};

const DispatcherMetrics: React.FC = () => (
  <ViewedProsProvider>
    <DispatcherMetricsContent />
  </ViewedProsProvider>
);

export default DispatcherMetrics;
